/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as HXKLineAndMultiIndicatorLineLayerSpec } from '../../../../layer/preset/hxKLine/hxKLineAndMultiIndicatorLine';
import { checkFieldType, createFieldValidator } from '../helper';
import * as utils from '../../../../util';
import { StockData } from '../../../../helper/hxKLine';

export type Spec = {
  layers: Partial<StandardChartLayerSpec>[];
};

type HXKLineAndMultiIndicatorLineLayerType = StandardChartLayerSpec &
  HXKLineAndMultiIndicatorLineLayerSpec & {
    __isEncodingValid?: boolean;
  };

// 定义数据值类型
type DataValue = Record<string, unknown>;

/**
 * 检查X轴字段类型是否符合要求
 * @param values 数据值数组
 * @param xField X轴字段名
 * @param layerType 图层类型
 * @returns 是否符合要求
 */
function validateXField(values: DataValue[], xField: string, layerType: string): boolean {
  if (!xField || !checkFieldType(values, xField, 'date')) {
    utils.logger.warn('warning', `The "x" field of the "${layerType}" layer must be a date type`);
    return false;
  }
  return true;
}

/**
 * 检查Y轴字段类型是否符合要求
 * @param values 数据值数组
 * @param yFields Y轴字段名数组
 * @param layerType 图层类型
 * @returns 是否符合要求
 */
function validateYFields(values: DataValue[], yFields: string[], layerType: string): boolean {
  if (!Array.isArray(yFields) || yFields.length === 0) {
    utils.logger.warn(
      'warning',
      `The "y" field array of the "${layerType}" layer must not be empty`
    );
    return false;
  }

  let isValid = true;
  yFields.forEach(yField => {
    if (!checkFieldType(values, yField, 'number')) {
      utils.logger.warn(
        'warning',
        `The "y" field "${yField}" of the "${layerType}" layer must be a number`
      );
      isValid = false;
    }
  });

  return isValid;
}

/**
 * 验证股票代码是否合法
 * @param code 股票代码
 * @returns 是否合法
 */
function isValidStockCode(code: string): boolean {
  return Boolean(code);
}

/**
 * 检查股票数据是否符合要求
 * @param stocks 股票数据数组
 * @param layerType 图层类型
 * @returns 是否符合要求
 */
function validateStocks(stocks: StockData[], layerType: string): boolean {
  if (!Array.isArray(stocks) || stocks.length === 0) {
    utils.logger.warn('warning', `The "stocks" configuration is required for "${layerType}" layer`);
    return false;
  }

  let isValid = true;
  stocks.forEach((stock, index) => {
    // 检查股票代码
    if (!isValidStockCode(stock.code)) {
      utils.logger.warn(
        'warning',
        `Invalid stock code at index ${index} in the "${layerType}" layer`
      );
      isValid = false;
    }

    // 检查市场代码
    if (!stock.market) {
      utils.logger.warn(
        'warning',
        `Missing market code at index ${index} in the "${layerType}" layer`
      );
      isValid = false;
    }

    // 检查股票名称
    if (!stock.name || typeof stock.name !== 'string') {
      utils.logger.warn(
        'warning',
        `Invalid stock name at index ${index} in the "${layerType}" layer`
      );
      isValid = false;
    }
  });

  return isValid;
}

/**
 * 检查K线叠加多指标折线图数据类型是否符合要求
 * @param layer 图层配置
 * @param data 数据
 * @returns 数据类型是否符合要求
 */
function validateDataTypes(layer: HXKLineAndMultiIndicatorLineLayerType, data: Data): boolean {
  const { encoding, stocks, type } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return false;
  }

  // 逐项验证数据类型
  const isXFieldValid = validateXField(values, encoding.x, type);
  const isYFieldsValid = validateYFields(values, encoding.y, type);
  const isStocksValid = validateStocks(stocks, type);

  return isXFieldValid && isYFieldsValid && isStocksValid;
}

/**
 * 验证K线叠加多指标折线图的编码配置
 * @param layer 图层配置
 * @returns 配置是否有效
 */
function validateEncoding(layer: HXKLineAndMultiIndicatorLineLayerType): boolean {
  return createFieldValidator('x')(layer) && createFieldValidator('y')(layer, true);
}

/**
 * K线叠加多指标折线图数据处理
 * @param data 数据
 * @param spec 配置
 * @returns 是否有效
 */
export function hxKLineAndMultiIndicatorLine(data: Data, spec: Spec): boolean {
  let isValid = true;
  const { layers = [] } = spec;

  if (layers.every(layer => layer.type !== 'hxKLineAndMultiIndicatorLine')) {
    return isValid;
  }

  layers
    .filter(
      (layer): layer is HXKLineAndMultiIndicatorLineLayerType =>
        layer.type === 'hxKLineAndMultiIndicatorLine'
    )
    .forEach(layer => {
      if (!validateEncoding(layer)) {
        isValid = false;
        layer.__isEncodingValid = false;
      }
      // 检查数据类型是否符合要求
      if (!validateDataTypes(layer, data)) {
        isValid = false;
        layer.__isEncodingValid = false;
        utils.logger.warn('warning', `Data type check failed for "${layer.type}" layer`);
      }
    });

  return isValid;
}
